import React, { useEffect, useState } from 'react';
import { Button, Grid, TextField, MenuItem } from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';

// Split a stored mobile (e.g. "+919876543210") into its country code and the
// local 10-digit number, so the edit form can prefill the Code dropdown.
function splitMobile(mobile) {
  const m = String(mobile || '').trim();
  if (m.startsWith('+1')) return { code: '+1', number: m.slice(2).replace(/\D/g, '') };
  if (m.startsWith('+61')) return { code: '+61', number: m.slice(3).replace(/\D/g, '') };
  if (m.startsWith('+91')) return { code: '+91', number: m.slice(3).replace(/\D/g, '') };
  return { code: '+91', number: m.replace(/\D/g, '') };
}

export default function UserForm(props) {
  const [isAddOperation, setIsAddOperation] = useState(false);
  const [recvValues, setRecvValues] = useState({
    id: '',
    username: '',
    password: '',
    mobile: '',
    mobile_country_code: '+91',
    role: '',
    issuer_id: '',
  });

  useEffect(() => {
    if (props.selectedRow !== null) {
      console.log('User Edit', JSON.stringify(props.selectedRow));
      const split = splitMobile(props.selectedRow.mobile);
      setRecvValues({
        id: props.selectedRow.id || '',
        username: props.selectedRow.username || '',
        password: props.selectedRow.password || '',
        mobile: split.number,
        mobile_country_code: split.code,
        role: props.selectedRow.role !== undefined ? String(props.selectedRow.role) : '',
        issuer_id: props.selectedRow.issuer_id !== undefined && props.selectedRow.issuer_id !== null ? String(props.selectedRow.issuer_id) : '',
      });
      setIsAddOperation(false);
    } else {
      setRecvValues({
        id: '',
        username: '',
        password: '',
        mobile: '',
        mobile_country_code: '+91',
        role: '',
        issuer_id: '',
      });
      setIsAddOperation(true);
    }
  }, [props.selectedRow]);

  const validationSchema = yup.object({
    username: yup
      .string()
      .required('Username is required'),
    password: yup
      .string()
      .min(6, 'Password must be at least 6 characters')
      .required('Password is required'),
    mobile: yup
      .string()
      .test('mobile-len', 'Mobile number must be 10 digits', (v) => !v || /^\d{10}$/.test(v)),
    role: yup
      .mixed()
      .required('Role is required'),
  });

  function onCancelForm() {
    props.handleSubmit(null, isAddOperation, true);
  }

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    validationSchema: validationSchema,
    initialValues: {
      id: recvValues?.id || '',
      username: recvValues?.username || '',
      password: recvValues?.password || '',
      mobile: recvValues?.mobile || '',
      mobile_country_code: recvValues?.mobile_country_code || '+91',
      role: recvValues?.role || '',
      issuer_id: recvValues?.issuer_id || '',
    },
    onSubmit: (values) => {
      // Convert values to correct types before submitting. The mobile is sent with
      // the selected country code prepended (e.g. +919876543210).
      const data = {
        username: values.username,
        password: values.password,
        mobile: values.mobile ? `${values.mobile_country_code}${values.mobile}` : '',
        role: Number(values.role),
        issuer_id: values.issuer_id ? Number(values.issuer_id) : null,
      };

      // The server identifies the user to update by username, not id, so do NOT
      // send an id field in the update payload.
      props.handleSubmit(data, isAddOperation, false);
    },
  });

  const isSubmitDisabled = formik.isSubmitting || !formik.dirty || !formik.isValid;

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid item container xs={12} spacing={2} justifyContent="flex-start" alignItems="flex-start" direction="row">
        <Grid item xs={12} sm={12} md={12}></Grid>
        
        <Grid item xs={12} sm={6} md={5}>
          <TextField
            required
            fullWidth
            variant="outlined"
            id="username"
            name="username"
            label="Username"
            disabled={!isAddOperation}
            value={formik.values.username}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            error={formik.touched.username && Boolean(formik.errors.username)}
            helperText={formik.touched.username && formik.errors.username}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={5}>
          <TextField
            required
            fullWidth
            variant="outlined"
            id="password"
            name="password"
            type="password"
            label="Password"
            value={formik.values.password}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
          />
        </Grid>

        {/* Country code + mobile number kept together on one line */}
        <Grid item xs={12} sm={6} md={5}>
          <Grid container spacing={1} alignItems="flex-start" wrap="nowrap">
            <Grid item style={{ width: 116, flexShrink: 0 }}>
              <TextField
                select
                fullWidth
                variant="outlined"
                id="mobile_country_code"
                name="mobile_country_code"
                label="Code"
                value={formik.values.mobile_country_code}
                onChange={formik.handleChange}
              >
                <MenuItem value="+91">+91 (IN)</MenuItem>
                <MenuItem value="+61">+61 (AU)</MenuItem>
                <MenuItem value="+1">+1 (US)</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs>
              <TextField
                fullWidth
                variant="outlined"
                id="mobile"
                name="mobile"
                label="Mobile Number"
                value={formik.values.mobile}
                onBlur={formik.handleBlur}
                onChange={(e) => formik.setFieldValue('mobile', e.target.value.replace(/\D/g, ''))}
                inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                error={formik.touched.mobile && Boolean(formik.errors.mobile)}
                helperText={(formik.touched.mobile && formik.errors.mobile) || '10 digits'}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} sm={6} md={5}>
          <TextField
            select
            required
            fullWidth
            variant="outlined"
            id="role"
            name="role"
            label="Role"
            value={formik.values.role}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            error={formik.touched.role && Boolean(formik.errors.role)}
            helperText={formik.touched.role && formik.errors.role}
          >
            <MenuItem value="">Select Role</MenuItem>
            <MenuItem value="110">Club admin</MenuItem>
            <MenuItem value="101">Club manager</MenuItem>
            <MenuItem value="102">Club Receptionist</MenuItem>
          </TextField>
        </Grid>

        {/* Associated Club / Partner dropdown hidden for now (not needed).
            The existing issuer_id is still preserved and sent unchanged on edit.
        {!isAddOperation && (
          <Grid item xs={12} sm={6} md={5}>
            <TextField
              select
              fullWidth
              variant="outlined"
              id="issuer_id"
              name="issuer_id"
              label="Associated Club / Partner"
              value={formik.values.issuer_id}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              error={formik.touched.issuer_id && Boolean(formik.errors.issuer_id)}
              helperText={formik.touched.issuer_id && formik.errors.issuer_id}
            >
              <MenuItem value="">None / All Clubs</MenuItem>
              {(props.RedemptionPartnerList || []).map((partner) => (
                <MenuItem key={partner.id} value={String(partner.id)}>
                  {partner.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
        */}
      </Grid>

      <Grid container item xs={12} direction="row" spacing={3} justifyContent="flex-start" style={{ marginTop: 12 }}>
        <Grid item>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitDisabled}
            style={{
              backgroundColor: isSubmitDisabled ? '#bdbdbd' : 'blue',
              color: isSubmitDisabled ? '#616161' : '#ffffff',
              opacity: 1,
              cursor: isSubmitDisabled ? 'not-allowed' : 'pointer',
            }}
          >
            Submit
          </Button>
        </Grid>
        <Grid item>
          <Button type="button" onClick={onCancelForm} color="secondary" variant="contained">
            Cancel
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}
